import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  AlertTriangle,
  Calendar,
  Car,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  CreditCard,
  DollarSign,
  Gamepad2,
  GraduationCap,
  HeartPulse,
  Home,
  Minus,
  Plane,
  Plus,
  Repeat,
  RotateCcw,
  Save,
  ScanSearch,
  Settings,
  Shield,
  ShoppingBag,
  Tag,
  Trash2,
  TrendingDown,
  TrendingUp,
  Utensils,
  Wallet,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { EmptyState } from '@/components/EmptyState'
import { Modal } from '@/components/Modal'
import { RangeSlider } from '@/components/RangeSlider'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FormField } from '@/components/FormField'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { useToast } from '@/hooks/useToast'
import { formatCurrency, formatDate, formatMonthYear } from '@/lib/locale'
import { cn } from '@/lib/cn'
import {
  useAIRecommendations,
  useAddBudgetItem,
  useAddRecurring,
  useBudgetActive,
  useBudgetAnalysis,
  useBudgetCategories,
  useBulkSaveBudgetItems,
  useCategoryTransactions,
  useDeleteBudgetItem,
  useDeleteRecurring,
  useDetectRecurring,
  useGenerateAlerts,
  useRecurringInsights,
  useRecurringTransactions,
  useResetBudget,
  useUpcomingPayments,
  useUpdateBudgetTotal,
} from '@/features/budget/queries'
import type {
  AIRecommendation,
  BudgetAnalysisRow,
  BudgetItem,
  BudgetItemStatus,
  DetectedRecurringCandidate,
} from '@/features/budget/types'

function toNum(v: number | string | null | undefined): number {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? 0))
  return Number.isFinite(n) ? n : 0
}

function categoryIcon(name: string): LucideIcon {
  const n = (name || '').toLowerCase()
  if (n.includes('housing') || n.includes('rent') || n.includes('mortgage')) return Home
  if (n.includes('food') || n.includes('dining') || n.includes('restaurant') || n.includes('grocery'))
    return Utensils
  if (n.includes('transport') || n.includes('gas') || n.includes('car') || n.includes('auto'))
    return Car
  if (n.includes('entertain') || n.includes('fun') || n.includes('game')) return Gamepad2
  if (n.includes('shop') || n.includes('retail') || n.includes('amazon')) return ShoppingBag
  if (n.includes('health') || n.includes('medical') || n.includes('gym')) return HeartPulse
  if (n.includes('education') || n.includes('tuition') || n.includes('school')) return GraduationCap
  if (n.includes('utility') || n.includes('electric') || n.includes('water') || n.includes('internet'))
    return Zap
  if (n.includes('insurance')) return Shield
  if (n.includes('subscription') || n.includes('membership')) return CreditCard
  if (n.includes('travel') || n.includes('hotel') || n.includes('flight')) return Plane
  if (n.includes('income') || n.includes('salary') || n.includes('paycheck')) return DollarSign
  return Tag
}

function statusClasses(status: BudgetItemStatus | undefined): { text: string; bg: string } {
  switch (status) {
    case 'over_budget':
      return { text: 'text-destructive', bg: 'bg-destructive/10' }
    case 'warning':
      return { text: 'text-warning-foreground', bg: 'bg-warning/15' }
    case 'under_budget':
      return { text: 'text-info', bg: 'bg-info/10' }
    case 'on_track':
    default:
      return { text: 'text-success', bg: 'bg-success/10' }
  }
}

function classifyStatus(spent: number, budgeted: number, alertThreshold = 80): BudgetItemStatus {
  if (budgeted <= 0) return 'on_track'
  const pct = (spent / budgeted) * 100
  if (pct >= 100) return 'over_budget'
  if (pct >= alertThreshold) return 'warning'
  if (pct < 50) return 'under_budget'
  return 'on_track'
}

function monthOptions(): Array<{ value: string; label: string; month: number; year: number }> {
  const now = new Date()
  const options: Array<{ value: string; label: string; month: number; year: number }> = []
  for (let i = -6; i <= 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1)
    const month = d.getMonth() + 1
    const year = d.getFullYear()
    options.push({
      value: `${month}-${year}`,
      label: formatMonthYear(d),
      month,
      year,
    })
  }
  return options
}

function SectionCard({
  title,
  actions,
  children,
  description,
  className,
}: {
  title: string
  actions?: ReactNode
  description?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <Card className={`p-4 sm:p-5${className ? ` ${className}` : ''}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-foreground sm:text-lg">{title}</h3>
          {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
      </div>
      {children}
    </Card>
  )
}

function SummaryCard({
  month,
  year,
  totalBudget,
  totalSpent,
  remaining,
  percentUsed,
  dailyBudget,
  daysRemaining,
  topCategories,
  allocated,
  overAllocated,
  onSetBudgetClick,
}: {
  month: number
  year: number
  totalBudget: number
  totalSpent: number
  remaining: number
  percentUsed: number
  dailyBudget: number
  daysRemaining: number
  topCategories: Array<{ category: string; percent: number }>
  allocated: number
  overAllocated: boolean
  onSetBudgetClick: () => void
}) {
  const periodLabel = formatMonthYear(new Date(year, month - 1, 1))
  const barColor =
    percentUsed >= 100 ? 'bg-destructive' : percentUsed >= 80 ? 'bg-warning' : 'bg-success'
  const pctColor =
    percentUsed >= 100
      ? 'text-destructive'
      : percentUsed >= 80
        ? 'text-warning-foreground'
        : 'text-primary'

  return (
    <Card className="p-4 sm:p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-foreground sm:text-lg">Budget Summary</h3>
        <Button type="button" variant="primary" size="sm" onClick={onSetBudgetClick}>
          <Wallet /> Set Monthly Budget
        </Button>
      </div>
      <div className="text-sm text-muted-foreground">{periodLabel}</div>
      <div className="mt-3 grid grid-cols-3 gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Total Budget</div>
          <div className="mt-1 text-lg font-semibold text-foreground">{formatCurrency(totalBudget)}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Total Spent</div>
          <div className="mt-1 text-lg font-semibold text-foreground">{formatCurrency(totalSpent)}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Remaining</div>
          <div
            className={cn(
              'mt-1 text-lg font-semibold',
              remaining < 0 ? 'text-destructive' : 'text-foreground',
            )}
          >
            {formatCurrency(Math.abs(remaining))}
          </div>
        </div>
      </div>
      <div className="mt-4">
        <div className="mb-1 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Budget Used</span>
          <span className={cn('font-semibold', pctColor)}>{percentUsed}%</span>
        </div>
        <div className="h-2.5 w-full rounded-pill bg-muted">
          <div className={cn('h-2.5 rounded-pill', barColor)} style={{ width: `${Math.min(percentUsed, 100)}%` }} />
        </div>
      </div>
      <div className="mt-4 rounded-md border border-border bg-muted/60 p-3">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Daily Budget</div>
        <div className="mt-1 text-lg font-semibold text-foreground">{formatCurrency(dailyBudget)}</div>
        <div className="text-xs text-muted-foreground">for the next {daysRemaining} days</div>
      </div>
      <div className="mt-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Allocation</div>
        <div className="mt-1 text-sm">
          <strong>{formatCurrency(allocated)}</strong> / {formatCurrency(totalBudget)}
          {overAllocated ? (
            <span className="ml-2 font-semibold text-destructive">(Over-allocated!)</span>
          ) : null}
        </div>
      </div>
      <div className="mt-4">
        <h4 className="mb-2 text-sm font-semibold text-foreground">Top Spending</h4>
        {topCategories.length === 0 ? (
          <p className="text-sm text-muted-foreground">No spending data</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {topCategories.map((c) => {
              const Icon = categoryIcon(c.category)
              return (
                <li key={c.category} className="flex items-center gap-3">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon className="size-4" aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">{c.category}</div>
                    <div className="h-1.5 w-full rounded-pill bg-muted">
                      <div className="h-1.5 rounded-pill bg-primary" style={{ width: `${c.percent}%` }} />
                    </div>
                  </div>
                  <span className="shrink-0 text-sm font-semibold text-foreground">{c.percent}%</span>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </Card>
  )
}

function CategoryCard({
  item,
  currentAmount,
  spent,
  sliderMax,
  onAmountChange,
  onDelete,
  onToggleTxns,
  expanded,
  txns,
}: {
  item: BudgetItem
  currentAmount: number
  spent: number
  sliderMax: number
  onAmountChange: (value: number) => void
  onDelete: (id: number) => void
  onToggleTxns: (id: number, category: string) => void
  expanded: boolean
  txns: Array<{ transaction_date: string; transaction_details: string; amount: number | string }> | undefined
}) {
  const name = item.category_name || item.account_name || 'Unknown'
  const pct = currentAmount > 0 ? Math.round((spent / currentAmount) * 100) : 0
  const status = classifyStatus(spent, currentAmount, item.alert_threshold ?? 80)
  const colors = statusClasses(status)
  const Icon = categoryIcon(name)
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className={cn('flex size-9 shrink-0 items-center justify-center rounded-md', colors.bg, colors.text)}>
            <Icon className="size-5" aria-hidden />
          </div>
          <span className="truncate text-sm font-semibold text-foreground">{name}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="whitespace-nowrap text-sm text-muted-foreground">
            {formatCurrency(spent)} / {formatCurrency(currentAmount)}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Delete"
            onClick={() => onDelete(item.id)}
            className="size-8"
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>
      <div className="mt-3">
        <div className="h-2 w-full rounded-pill bg-muted">
          <div
            className={cn('h-2 rounded-pill', colors.text.replace('text-', 'bg-'))}
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        </div>
        <div className="mt-1 text-right text-xs">
          <span className={colors.text}>{pct}%</span>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <RangeSlider
          min={0}
          max={sliderMax}
          step={50}
          value={currentAmount}
          onChange={onAmountChange}
          ariaLabel={`Budget amount for ${name}`}
          className="flex-1"
        />
        <span className="w-20 shrink-0 text-right text-sm font-semibold text-foreground">
          {formatCurrency(currentAmount)}
        </span>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => onToggleTxns(item.id, name)}
        className="mt-3 w-full justify-center"
      >
        View Transactions {expanded ? <ChevronUp /> : <ChevronDown />}
      </Button>
      {expanded ? (
        <div className="mt-2 space-y-1">
          {!txns ? (
            <LoadingSpinner />
          ) : txns.length === 0 ? (
            <p className="text-center text-xs text-muted-foreground">No transactions found.</p>
          ) : (
            txns.map((t, i) => (
              <div
                key={i}
                className="flex items-center justify-between gap-2 border-b border-border/50 py-1 text-xs last:border-b-0"
              >
                <span className="shrink-0 text-muted-foreground">
                  {formatDate(t.transaction_date, { month: 'short', day: 'numeric' })}
                </span>
                <span className="min-w-0 flex-1 truncate">{t.transaction_details}</span>
                <span className="shrink-0 font-semibold">{formatCurrency(toNum(t.amount))}</span>
              </div>
            ))
          )}
        </div>
      ) : null}
    </Card>
  )
}

function AnalysisCard({ a }: { a: BudgetAnalysisRow }) {
  const hasBudget = a.budgeted > 0
  const pct = hasBudget ? Math.min(Math.round((a.spent / a.budgeted) * 100), 100) : 0
  const colors = statusClasses(a.status)
  const StatusIcon =
    a.status === 'over_budget' || a.status === 'warning' ? AlertTriangle :
    a.status === 'under_budget' ? TrendingDown : CheckCircle2
  const TrendIcon = a.trend === 'increasing' ? TrendingUp : a.trend === 'decreasing' ? TrendingDown : Minus
  return (
    <Card className="p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <StatusIcon className={cn('size-4', colors.text)} />
          <span className="font-semibold text-foreground">{a.category}</span>
        </div>
        {hasBudget ? (
          <span className={cn('rounded-pill px-2 py-0.5 text-xs font-semibold uppercase', colors.bg, colors.text)}>
            {a.status?.replace('_', ' ') ?? 'status'}
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-4 text-sm">
        <div>
          <span className="text-muted-foreground">Spent: </span>
          <span className="font-semibold">{formatCurrency(a.spent)}</span>
        </div>
        {hasBudget ? (
          <div>
            <span className="text-muted-foreground">Budget: </span>
            <span className="font-semibold">{formatCurrency(a.budgeted)}</span>
          </div>
        ) : null}
      </div>
      {hasBudget ? (
        <div className="mt-2 h-1.5 w-full rounded-pill bg-muted">
          <div
            className={cn('h-1.5 rounded-pill', colors.text.replace('text-', 'bg-'))}
            style={{ width: `${pct}%` }}
          />
        </div>
      ) : null}
      <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
        <span>Projected: {formatCurrency(a.projected)}</span>
        {a.trend ? (
          <span className="flex items-center gap-1">
            <TrendIcon className="size-3" /> {a.trend}
          </span>
        ) : null}
      </div>
    </Card>
  )
}

function SuggestionCard({
  rec,
  onApply,
}: {
  rec: AIRecommendation
  onApply: (rec: AIRecommendation) => void
}) {
  const Icon = rec.type === 'reduce' ? TrendingDown : rec.type === 'increase' ? TrendingUp : Check
  const label = rec.type === 'reduce' ? 'Reduce Budget' : rec.type === 'increase' ? 'Increase Budget' : 'Keep Current'
  return (
    <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Icon className="size-5" aria-hidden />
        </div>
        <div className="min-w-0">
          <h4 className="font-semibold text-foreground">{rec.category}</h4>
          <p className="text-sm text-muted-foreground">{rec.message}</p>
        </div>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={() => onApply(rec)}>
        <Icon /> {label}
      </Button>
    </Card>
  )
}

function PaymentCard({
  name,
  category,
  amount,
  dueDate,
  daysUntil,
  urgency,
  recurrenceLabel,
  onDelete,
}: {
  name: string
  category?: string | null
  amount: number
  dueDate: string
  daysUntil: number
  urgency: 'due_soon' | 'this_week' | 'upcoming'
  recurrenceLabel: string
  onDelete?: () => void
}) {
  const urgencyText =
    urgency === 'due_soon'
      ? 'text-destructive'
      : urgency === 'this_week'
        ? 'text-warning-foreground'
        : 'text-muted-foreground'
  const urgencyLabel =
    urgency === 'due_soon' ? 'Due Soon' : urgency === 'this_week' ? 'This Week' : 'Upcoming'
  return (
    <Card className="flex items-center gap-3 p-3">
      <div className="flex w-16 shrink-0 flex-col items-start text-xs">
        <div className="flex items-center gap-1 font-semibold text-foreground">
          <Clock className="size-3" />
          {daysUntil === 0 ? 'Today' : `${daysUntil}d`}
        </div>
        <span className={cn('text-[0.7rem] font-medium uppercase', urgencyText)}>{urgencyLabel}</span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <span className="truncate">{name}</span>
          <span className="inline-flex items-center gap-1 rounded-pill bg-muted px-2 py-0.5 text-[0.7rem] text-muted-foreground">
            <Repeat className="size-3" /> {recurrenceLabel}
          </span>
        </div>
        <div className="truncate text-xs text-muted-foreground">
          {category ? `${category} • ` : ''}Next {formatDate(dueDate, { month: 'short', day: 'numeric', year: 'numeric' })}
        </div>
      </div>
      <div className={cn('shrink-0 text-right text-sm font-bold', urgencyText)}>{formatCurrency(amount)}</div>
      {onDelete ? (
        <Button type="button" variant="ghost" size="icon" aria-label="Remove" onClick={onDelete} className="size-8">
          <Trash2 className="size-4" />
        </Button>
      ) : null}
    </Card>
  )
}

export function BudgetPlanningPage() {
  const months = useMemo(monthOptions, [])
  const [monthValue, setMonthValue] = useState<string>(() => {
    const now = new Date()
    return `${now.getMonth() + 1}-${now.getFullYear()}`
  })
  const selected = months.find((m) => m.value === monthValue) ?? months[6]
  const month = selected.month
  const year = selected.year

  const { data: active, isLoading: activeLoading } = useBudgetActive(month, year)
  const { data: analysis, isLoading: analysisLoading } = useBudgetAnalysis(month, year)
  const { data: recommendations } = useAIRecommendations(month, year)
  const { data: upcoming } = useUpcomingPayments(14)
  const { data: recurring } = useRecurringTransactions()
  const { data: categoriesData } = useBudgetCategories()

  // Mutations
  const updateTotal = useUpdateBudgetTotal()
  const bulkSave = useBulkSaveBudgetItems()
  const reset = useResetBudget()
  const deleteItemMut = useDeleteBudgetItem()
  const addItemMut = useAddBudgetItem()
  const generateAlerts = useGenerateAlerts()
  const addRecurring = useAddRecurring()
  const deleteRecurringMut = useDeleteRecurring()
  const detectRecurring = useDetectRecurring()
  const recurringInsightsMut = useRecurringInsights()

  const { toast } = useToast()

  // Fire-and-forget alert generation whenever the period changes.
  useEffect(() => {
    generateAlerts.mutate({ month, year })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year])

  // Slider local state - map of item id → draft amount (falls back to item.budgeted_amount).
  const [drafts, setDrafts] = useState<Record<number, number>>({})
  useEffect(() => {
    setDrafts({})
  }, [monthValue, active?.items.length])

  // Sync the right-column (Budget Transaction Types) max-height to the
  // left-column (Budget Summary) height so the right card never grows past
  // the left and overflowing items scroll internally. Only applies at lg+
  // where the two columns sit side-by-side - on mobile they stack and the
  // cap is irrelevant.
  const summaryColRef = useRef<HTMLDivElement>(null)
  const [summaryHeight, setSummaryHeight] = useState<number | null>(null)
  useLayoutEffect(() => {
    const el = summaryColRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      setSummaryHeight(el.offsetHeight || null)
    })
    ro.observe(el)
    setSummaryHeight(el.offsetHeight || null)
    return () => ro.disconnect()
  }, [])

  const items = active?.items ?? []
  const totalBudget = toNum(active?.budget?.total_amount)
  const totalSpent = toNum(active?.budget?.total_spent)

  function currentAmount(it: BudgetItem): number {
    return drafts[it.id] ?? toNum(it.budgeted_amount)
  }

  function totalAllocated(): number {
    return items.reduce((sum, i) => sum + currentAmount(i), 0)
  }

  function sliderMax(it: BudgetItem): number {
    if (totalBudget <= 0) return Math.max(currentAmount(it) * 3, 5000)
    const others = items.reduce((sum, i) => (i.id === it.id ? sum : sum + currentAmount(i)), 0)
    return Math.max(currentAmount(it), totalBudget - others)
  }

  function setDraft(id: number, val: number) {
    setDrafts((d) => ({ ...d, [id]: val }))
  }

  // Live-updated summary values (reflect slider drafts).
  const allocated = totalAllocated()
  const overAllocated = totalBudget > 0 && allocated > totalBudget
  const liveRemaining = totalBudget - totalSpent
  const livePercent = totalBudget > 0 ? Math.round((totalSpent / totalBudget) * 100) : 0
  const now = new Date()
  const daysInMonth = new Date(year, month, 0).getDate()
  const dayOfMonth = Math.min(now.getDate(), daysInMonth)
  const daysRemaining = Math.max(1, daysInMonth - dayOfMonth)
  const dailyBudget = liveRemaining > 0 ? Math.round(liveRemaining / daysRemaining) : 0

  // Modal state
  const [setBudgetOpen, setSetBudgetOpen] = useState(false)
  const [budgetInput, setBudgetInput] = useState('')
  const [addTypeOpen, setAddTypeOpen] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [newBudgetAmount, setNewBudgetAmount] = useState('')
  const [newAlertThreshold, setNewAlertThreshold] = useState('80')
  const [saveOpen, setSaveOpen] = useState(false)
  const [resetOpen, setResetOpen] = useState(false)
  const [deleteItemId, setDeleteItemId] = useState<number | null>(null)
  const [recurringModalOpen, setRecurringModalOpen] = useState(false)
  const [deleteRecurringId, setDeleteRecurringId] = useState<number | null>(null)
  const [detectedCandidates, setDetectedCandidates] = useState<DetectedRecurringCandidate[] | null>(null)
  // Tracks which detected-candidate indices the user has already added, so
  // the "Add" button flips to a disabled green "Added" badge and rapid
  // double-clicks can't duplicate the row (the server now rejects dupes too,
  // but UX-wise this is the clear signal).
  const [addedCandidateIdx, setAddedCandidateIdx] = useState<Set<number>>(() => new Set())
  const [pendingCandidateIdx, setPendingCandidateIdx] = useState<number | null>(null)
  const [showAddRecurringForm, setShowAddRecurringForm] = useState(false)
  const [recName, setRecName] = useState('')
  const [recAmount, setRecAmount] = useState('')
  const [recCategory, setRecCategory] = useState('')
  const [recType, setRecType] = useState<'monthly' | 'weekly' | 'biweekly'>('monthly')
  const [recNextDue, setRecNextDue] = useState('')

  // Expanded-transactions state
  const [expandedCategory, setExpandedCategory] = useState<{ id: number; name: string } | null>(null)
  const { data: categoryTxns } = useCategoryTransactions(
    expandedCategory?.name ?? null,
    month,
    year,
  )

  // AI recurring insights
  const [insights, setInsights] = useState<string[] | null>(null)
  useEffect(() => {
    const items = recurring?.recurring_transactions ?? []
    if (items.length === 0) {
      setInsights(null)
      return
    }
    let cancelled = false
    recurringInsightsMut
      .mutateAsync(items)
      .then((r) => {
        if (!cancelled) setInsights(r.insights ?? [])
      })
      .catch(() => {
        if (!cancelled) setInsights(null)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recurring?.recurring_transactions?.length])

  function openSetBudget() {
    setBudgetInput('')
    setSetBudgetOpen(true)
  }

  async function handleUpdateTotal() {
    if (!active?.budget) return
    const amount = parseFloat(budgetInput) || 0
    try {
      await updateTotal.mutateAsync({ budgetId: active.budget.id, totalAmount: amount })
      toast({ title: 'Monthly budget updated!' })
      setSetBudgetOpen(false)
    } catch {
      toast({ title: 'Failed to update budget', variant: 'destructive' })
    }
  }

  async function handleBulkSave() {
    if (!active?.budget) return
    const payload = items.map((it) => ({
      item_type: it.item_type,
      category_name: it.category_name,
      account_code: it.account_code ?? undefined,
      account_name: it.account_name ?? undefined,
      budgeted_amount: currentAmount(it),
      alert_threshold: it.alert_threshold ?? undefined,
      icon: it.icon ?? undefined,
      color: it.color ?? undefined,
    }))
    try {
      await bulkSave.mutateAsync({ budgetId: active.budget.id, items: payload })
      toast({ title: 'Budget saved successfully!' })
      setSaveOpen(false)
      setDrafts({})
    } catch {
      toast({ title: 'Failed to save changes', variant: 'destructive' })
    }
  }

  async function handleReset() {
    if (!active?.budget) return
    try {
      await reset.mutateAsync(active.budget.id)
      toast({ title: 'Budget reset successfully.' })
      setResetOpen(false)
    } catch {
      toast({ title: 'Failed to reset', variant: 'destructive' })
    }
  }

  async function handleDeleteItem() {
    if (deleteItemId === null) return
    try {
      await deleteItemMut.mutateAsync(deleteItemId)
      toast({ title: 'Transaction type removed.' })
      setDeleteItemId(null)
    } catch {
      toast({ title: 'Failed to delete', variant: 'destructive' })
    }
  }

  async function handleAddItem() {
    if (!active?.budget) return
    if (totalBudget <= 0) {
      toast({
        title: 'Please set a total budget first before adding transaction types.',
        variant: 'destructive',
      })
      return
    }
    const requested = parseFloat(newBudgetAmount) || 0
    const remaining = totalBudget - totalAllocated()
    if (remaining <= 0) {
      toast({ title: 'No budget remaining! Please increase your total budget first.', variant: 'destructive' })
      return
    }
    const amount = requested > remaining ? remaining : requested
    if (requested > remaining) {
      toast({
        title: `Amount exceeds remaining budget (${formatCurrency(remaining)}). Capping to available amount.`,
        variant: 'destructive',
      })
    }
    try {
      await addItemMut.mutateAsync({
        budgetId: active.budget.id,
        item: {
          item_type: 'category',
          category_name: newCategoryName,
          budgeted_amount: amount,
          alert_threshold: parseInt(newAlertThreshold, 10) || 80,
        },
      })
      toast({ title: 'Transaction type added!' })
      setAddTypeOpen(false)
      setNewBudgetAmount('')
      setNewCategoryName('')
      setNewAlertThreshold('80')
    } catch {
      toast({ title: 'Failed to add transaction type', variant: 'destructive' })
    }
  }

  function applySuggestion(rec: AIRecommendation) {
    const item = items.find((i) => i.category_name === rec.category)
    if (!item) {
      toast({ title: 'Transaction type not found in budget.', variant: 'destructive' })
      return
    }
    const current = currentAmount(item)
    const change = rec.suggested_change ?? 0
    let newAmount = current
    if (rec.type === 'reduce') newAmount = Math.max(0, current - change)
    else if (rec.type === 'increase') {
      newAmount = current + (change || 50)
      if (totalBudget > 0) {
        const others = items.reduce((sum, i) => (i.id === item.id ? sum : sum + currentAmount(i)), 0)
        const max = totalBudget - others
        if (newAmount > max) {
          if (max <= current) {
            toast({
              title: `No budget remaining to increase ${rec.category}. Please increase your total budget first.`,
              variant: 'destructive',
            })
            return
          }
          newAmount = max
          toast({
            title: `Capped to remaining budget: ${formatCurrency(newAmount)}`,
            variant: 'destructive',
          })
        }
      }
    } else {
      return
    }
    setDraft(item.id, newAmount)
    toast({ title: `Updated ${rec.category} budget to ${formatCurrency(newAmount)}` })
  }

  async function handleToggleTxns(id: number, name: string) {
    setExpandedCategory((prev) => (prev?.id === id ? null : { id, name }))
  }

  function openRecurring() {
    setDetectedCandidates(null)
    setAddedCandidateIdx(new Set())
    setPendingCandidateIdx(null)
    setShowAddRecurringForm(false)
    setRecurringModalOpen(true)
  }

  async function handleDetectRecurring() {
    try {
      const res = await detectRecurring.mutateAsync()
      setAddedCandidateIdx(new Set())
      setPendingCandidateIdx(null)
      if ((res.candidates ?? []).length === 0) {
        toast({ title: 'No recurring patterns found yet.' })
        setDetectedCandidates([])
      } else {
        setDetectedCandidates(res.candidates)
        toast({ title: `Found ${res.candidates.length} recurring pattern(s)!` })
      }
    } catch {
      toast({ title: 'Detection failed. Please try again.', variant: 'destructive' })
    }
  }

  function autoCalcNextDue(type: 'weekly' | 'biweekly' | 'monthly'): string {
    const d = new Date()
    if (type === 'weekly') d.setDate(d.getDate() + 7)
    else if (type === 'biweekly') d.setDate(d.getDate() + 14)
    else d.setMonth(d.getMonth() + 1)
    return d.toISOString().slice(0, 10)
  }

  useEffect(() => {
    if (!recNextDue) setRecNextDue(autoCalcNextDue(recType))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recType])

  async function handleSaveDetected(c: DetectedRecurringCandidate, index: number) {
    if (addedCandidateIdx.has(index) || pendingCandidateIdx !== null) return
    setPendingCandidateIdx(index)
    try {
      await addRecurring.mutateAsync({
        name: c.name,
        amount: c.amount,
        category: c.category ?? null,
        recurrence_type: c.recurrence_type,
        next_due_date: c.next_expected_date,
      })
      setAddedCandidateIdx((prev) => {
        const next = new Set(prev)
        next.add(index)
        return next
      })
      toast({ title: `Added: ${c.name}` })
    } catch {
      toast({ title: 'Failed to add', variant: 'destructive' })
    } finally {
      setPendingCandidateIdx(null)
    }
  }

  async function handleAddRecurring() {
    if (!recName.trim()) return toast({ title: 'Please enter a name.', variant: 'destructive' })
    const amt = parseFloat(recAmount)
    if (!amt) return toast({ title: 'Please enter an amount.', variant: 'destructive' })
    if (!recCategory) return toast({ title: 'Please select a category.', variant: 'destructive' })
    if (!recNextDue) return toast({ title: 'Please set the next due date.', variant: 'destructive' })
    try {
      await addRecurring.mutateAsync({
        name: recName,
        amount: amt,
        category: recCategory,
        recurrence_type: recType,
        next_due_date: recNextDue,
      })
      toast({ title: 'Recurring transaction added!' })
      setRecName('')
      setRecAmount('')
      setRecCategory('')
      setRecType('monthly')
      setRecNextDue('')
      setShowAddRecurringForm(false)
    } catch {
      toast({ title: 'Failed to add', variant: 'destructive' })
    }
  }

  async function handleDeleteRecurring() {
    if (deleteRecurringId === null) return
    try {
      await deleteRecurringMut.mutateAsync(deleteRecurringId)
      toast({ title: 'Deleted.' })
      setDeleteRecurringId(null)
    } catch {
      toast({ title: 'Failed to delete', variant: 'destructive' })
    }
  }

  const recurringList = recurring?.recurring_transactions ?? []

  const actions = (
    <div className="flex items-center gap-2">
      <span className="text-sm text-muted-foreground">Monthly Budget:</span>
      <Select value={monthValue} onValueChange={setMonthValue}>
        <SelectTrigger className="w-[170px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {months.map((m) => (
            <SelectItem key={m.value} value={m.value}>
              {m.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )

  return (
    <Page title="Budget Planning" description="Set your monthly budget and track spending by category." actions={actions} width="wide">
      {overAllocated ? (
        <Alert variant="destructive">
          <AlertDescription>
            You&apos;ve allocated {formatCurrency(allocated)} of a {formatCurrency(totalBudget)} total budget.
            Reduce allocations or raise the total to balance.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
        <div className="lg:col-span-1" ref={summaryColRef}>
          <SummaryCard
            month={month}
            year={year}
            totalBudget={totalBudget}
            totalSpent={totalSpent}
            remaining={liveRemaining}
            percentUsed={livePercent}
            dailyBudget={dailyBudget}
            daysRemaining={daysRemaining}
            topCategories={active?.top_categories ?? []}
            allocated={allocated}
            overAllocated={overAllocated}
            onSetBudgetClick={openSetBudget}
          />
        </div>
        <div
          className="flex min-h-0 flex-col gap-3 lg:col-span-2"
          style={summaryHeight ? { maxHeight: `${summaryHeight}px` } : undefined}
        >
          <SectionCard
            title="Budget Transaction Types"
            description={`Allocated: ${formatCurrency(allocated)} / ${formatCurrency(totalBudget)}`}
            actions={
              <>
                <Button type="button" variant="primary" size="sm" onClick={() => setAddTypeOpen(true)}>
                  <Plus /> Add Type
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setResetOpen(true)}>
                  <RotateCcw /> Reset All
                </Button>
              </>
            }
            className="flex min-h-0 flex-1 flex-col"
          >
            {activeLoading ? (
              <LoadingSpinner />
            ) : items.length === 0 ? (
              <EmptyState title="No budget transaction types set" description='Click "Add Type" to get started.' />
            ) : (
              <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
                {items.map((it) => (
                  <CategoryCard
                    key={it.id}
                    item={it}
                    currentAmount={currentAmount(it)}
                    spent={it.spent ?? 0}
                    sliderMax={sliderMax(it)}
                    onAmountChange={(v) => setDraft(it.id, v)}
                    onDelete={(id) => setDeleteItemId(id)}
                    onToggleTxns={handleToggleTxns}
                    expanded={expandedCategory?.id === it.id}
                    txns={
                      expandedCategory?.id === it.id
                        ? categoryTxns?.transactions
                        : undefined
                    }
                  />
                ))}
              </div>
            )}
            {items.length > 0 ? (
              <div className="mt-3 flex justify-end">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => setSaveOpen(true)}
                  disabled={Object.keys(drafts).length === 0}
                >
                  <Save /> Save Budget Adjustments
                </Button>
              </div>
            ) : null}
          </SectionCard>
        </div>
      </div>

      <SectionCard title="Transaction Type Analysis">
        {analysisLoading ? (
          <LoadingSpinner />
        ) : !analysis || analysis.analysis.length === 0 ? (
          <EmptyState title="No analysis" description="No analysis data available yet." />
        ) : (
          // Cap at ~4 rows of cards (2 per row on md+) and let the rest scroll
          // so the section doesn't push everything below it off the page.
          <div className="max-h-[32rem] overflow-y-auto pr-1">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {analysis.analysis.map((a) => (
                <AnalysisCard key={a.category} a={a} />
              ))}
            </div>
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="Budget Suggestions"
        description="AI-powered recommendations to optimize your spending."
      >
        {!recommendations || recommendations.recommendations.length === 0 ? (
          <EmptyState title="No suggestions yet" description="Add budget categories and spending data to get personalized recommendations." />
        ) : (
          <div className="flex flex-col gap-3">
            {recommendations.recommendations.map((r, i) => (
              <SuggestionCard key={i} rec={r} onApply={applySuggestion} />
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="Upcoming Payments"
        description="Bills due in the next 2 weeks."
        actions={
          upcoming && upcoming.payments.length > 0 ? (
            <span className="text-sm">
              Total upcoming: <strong>{formatCurrency(upcoming.total_upcoming)}</strong>
            </span>
          ) : null
        }
      >
        {!upcoming || upcoming.payments.length === 0 ? (
          <EmptyState
            icon={Calendar}
            title="No upcoming payments"
            description="Add recurring transactions below to track bills."
          />
        ) : (
          <div className="flex flex-col gap-2">
            {upcoming.payments.map((p, i) => (
              <PaymentCard
                key={`${p.name}-${i}`}
                name={p.name}
                category={p.category}
                amount={toNum(p.amount)}
                dueDate={p.next_due_date}
                daysUntil={p.days_until}
                urgency={p.urgency}
                recurrenceLabel="Recurring"
              />
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="Recurring Transactions"
        description="Your tracked recurring bills and subscriptions."
        actions={
          <Button type="button" variant="primary" size="sm" onClick={openRecurring}>
            <Settings /> Manage
          </Button>
        }
      >
        {recurringList.length === 0 ? (
          <EmptyState
            icon={Repeat}
            title="No recurring transactions tracked yet"
            action={
              <Button type="button" variant="primary" size="sm" onClick={openRecurring}>
                <Plus /> Add Recurring Transaction
              </Button>
            }
          />
        ) : (
          <div className="flex flex-col gap-2">
            {recurringList.map((rt) => {
              const next = rt.next_due_date ? new Date(rt.next_due_date) : null
              const daysUntil = next ? Math.ceil((next.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null
              const urgency: 'due_soon' | 'this_week' | 'upcoming' =
                daysUntil === null
                  ? 'upcoming'
                  : daysUntil <= 3
                    ? 'due_soon'
                    : daysUntil <= 7
                      ? 'this_week'
                      : 'upcoming'
              return (
                <PaymentCard
                  key={rt.id}
                  name={rt.name}
                  category={rt.category}
                  amount={toNum(rt.amount)}
                  dueDate={rt.next_due_date ?? ''}
                  daysUntil={daysUntil ?? 0}
                  urgency={urgency}
                  recurrenceLabel={rt.recurrence_type}
                  onDelete={() => setDeleteRecurringId(rt.id)}
                />
              )
            })}
          </div>
        )}
        {insights && insights.length > 0 ? (
          <div className="mt-3 rounded-md border border-border bg-muted/50 p-3">
            <h4 className="mb-1 text-sm font-semibold text-foreground">AI Recurring Payment Insights</h4>
            <ul className="list-inside list-disc text-sm text-muted-foreground">
              {insights.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </SectionCard>

      {/* Set Monthly Budget Modal */}
      <Modal
        open={setBudgetOpen}
        onOpenChange={setSetBudgetOpen}
        title="Set Monthly Budget"
        description="Set your total spending limit for the selected month."
        footer={
          <>
            <Button variant="secondary" onClick={() => setSetBudgetOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleUpdateTotal} disabled={updateTotal.isPending}>
              <Check /> Set Budget
            </Button>
          </>
        }
      >
        <FormField
          id="set-budget-input"
          label="Monthly Budget Amount ($)"
          type="number"
          min={0}
          step={100}
          value={budgetInput}
          onChange={(e) => setBudgetInput(e.currentTarget.value)}
          placeholder="e.g. 5000"
          hint="Enter your total monthly budget."
        />
      </Modal>

      {/* Add Transaction Type Modal */}
      <Modal
        open={addTypeOpen}
        onOpenChange={setAddTypeOpen}
        title="Add Transaction Type"
        description="Add a spending category to your budget."
        footer={
          <>
            <Button variant="secondary" onClick={() => setAddTypeOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAddItem} disabled={addItemMut.isPending}>
              <Plus /> Add Transaction Type
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <FormField
            id="new-cat"
            label="Transaction Type"
            control={
              <Select value={newCategoryName} onValueChange={setNewCategoryName}>
                <SelectTrigger id="new-cat">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {(categoriesData?.categories ?? []).map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
          />
          <FormField
            id="new-amt"
            label="Budget Amount ($)"
            type="number"
            min={0}
            step={50}
            value={newBudgetAmount}
            onChange={(e) => setNewBudgetAmount(e.currentTarget.value)}
            placeholder="e.g. 500"
            hint={
              totalBudget <= 0
                ? 'No total budget set. Please set a total budget first.'
                : totalBudget - allocated <= 0
                  ? 'No budget remaining! Increase your total budget to add more.'
                  : `Remaining budget available: ${formatCurrency(totalBudget - allocated)}`
            }
          />
          <FormField
            id="new-alert"
            label="Alert Threshold (%)"
            type="number"
            min={1}
            max={100}
            value={newAlertThreshold}
            onChange={(e) => setNewAlertThreshold(e.currentTarget.value)}
            hint="You'll be notified when spending reaches this percentage of the budget."
          />
        </div>
      </Modal>

      <Modal.Confirm
        open={saveOpen}
        onOpenChange={setSaveOpen}
        title="Save Budget Adjustments"
        body="Are you sure you want to save all your budget adjustments? This will update the allocated amounts for each transaction type."
        confirmLabel="Yes, Save Changes"
        confirmVariant="primary"
        onConfirm={handleBulkSave}
        busy={bulkSave.isPending}
      />

      <Modal.Confirm
        open={resetOpen}
        onOpenChange={setResetOpen}
        title="Reset All Budgets"
        body="This action cannot be undone. All budget categories and their allocated amounts will be permanently removed for this month."
        confirmLabel="Yes, Reset Everything"
        onConfirm={handleReset}
        busy={reset.isPending}
      />

      <Modal.Confirm
        open={deleteItemId !== null}
        onOpenChange={(open) => !open && setDeleteItemId(null)}
        title="Delete Transaction Type?"
        body="This transaction type will be removed from your budget for this month."
        confirmLabel="Delete"
        onConfirm={handleDeleteItem}
        busy={deleteItemMut.isPending}
      />

      <Modal.Confirm
        open={deleteRecurringId !== null}
        onOpenChange={(open) => !open && setDeleteRecurringId(null)}
        title="Delete Recurring Transaction?"
        body="This recurring bill will stop being tracked."
        confirmLabel="Delete"
        onConfirm={handleDeleteRecurring}
        busy={deleteRecurringMut.isPending}
      />

      {/* Recurring Modal */}
      <Modal
        open={recurringModalOpen}
        onOpenChange={setRecurringModalOpen}
        title="Recurring Transactions"
        description="Manage your recurring bills and subscriptions."
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleDetectRecurring}
              disabled={detectRecurring.isPending}
            >
              <ScanSearch /> Detect Recurring
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowAddRecurringForm((v) => !v)}
            >
              <Plus /> Add Manual
            </Button>
          </div>
          {detectedCandidates === null ? (
            recurringList.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground">
                No recurring transactions yet. Click &quot;Detect Recurring&quot; to scan your history.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {recurringList.map((rt) => (
                  <li key={rt.id} className="flex items-center justify-between gap-3 rounded-md border border-border p-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold">{rt.name}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {rt.recurrence_type} • {formatCurrency(toNum(rt.amount))} • Next{' '}
                        {rt.next_due_date
                          ? formatDate(rt.next_due_date, { month: 'short', day: 'numeric' })
                          : 'N/A'}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteRecurringId(rt.id)}
                      aria-label="Delete"
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )
          ) : detectedCandidates.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground">No recurring patterns detected.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {detectedCandidates.map((c, i) => {
                const added = addedCandidateIdx.has(i)
                const pending = pendingCandidateIdx === i
                return (
                  <li key={i} className="flex items-center justify-between gap-3 rounded-md border border-border p-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold">{c.name}</div>
                      <div className="text-xs text-muted-foreground">
                        <strong>{c.recurrence_type}</strong> • ~{formatCurrency(c.amount)} per occurrence • seen{' '}
                        {c.occurrence_count} times
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Next expected: <strong>{c.next_expected_date}</strong>
                      </div>
                    </div>
                    {added ? (
                      <Button
                        type="button"
                        size="sm"
                        disabled
                        className="bg-success text-success-foreground hover:bg-success disabled:opacity-100"
                      >
                        <Check /> Added
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        disabled={pending || pendingCandidateIdx !== null}
                        onClick={() => handleSaveDetected(c, i)}
                      >
                        {pending ? 'Adding…' : 'Add'}
                      </Button>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
          {showAddRecurringForm ? (
            <div className="mt-2 flex flex-col gap-3 border-t border-border pt-3">
              <FormField
                id="rec-name"
                label="Name"
                value={recName}
                onChange={(e) => setRecName(e.currentTarget.value)}
                placeholder="e.g. Electric Bill, Netflix, Rent"
              />
              <FormField
                id="rec-amt"
                label="Amount ($)"
                type="number"
                min={0}
                step={0.01}
                value={recAmount}
                onChange={(e) => setRecAmount(e.currentTarget.value)}
                placeholder="0"
              />
              <FormField
                id="rec-cat"
                label="Category"
                control={
                  <Select value={recCategory} onValueChange={setRecCategory}>
                    <SelectTrigger id="rec-cat">
                      <SelectValue placeholder="-- Select category --" />
                    </SelectTrigger>
                    <SelectContent>
                      {(categoriesData?.categories ?? []).map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                }
              />
              <FormField
                id="rec-type"
                label="Recurrence"
                control={
                  <Select
                    value={recType}
                    onValueChange={(v) => {
                      const t = v as 'monthly' | 'weekly' | 'biweekly'
                      setRecType(t)
                      setRecNextDue(autoCalcNextDue(t))
                    }}
                  >
                    <SelectTrigger id="rec-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Biweekly</SelectItem>
                    </SelectContent>
                  </Select>
                }
              />
              <FormField
                id="rec-date"
                label="Next Due Date"
                type="date"
                value={recNextDue}
                onChange={(e) => setRecNextDue(e.currentTarget.value)}
                hint="Auto-filled based on recurrence. Adjust if needed."
              />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="secondary" size="sm" onClick={() => setShowAddRecurringForm(false)}>
                  Cancel
                </Button>
                <Button type="button" variant="primary" size="sm" onClick={handleAddRecurring}>
                  <Plus /> Add
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </Modal>
    </Page>
  )
}
